import { useEffect, useState } from "react";
import { Button, Card, Cluster, FormField, Grid, Modal, Select, Stack } from "@salesapp/ui";
import { TipHeading, TipKicker } from "./AdminTip";
import { api, ApiError } from "./api";
import { formatUtc } from "./format";
import { Flash } from "./Flash";

interface Attempt {
  at: string;
  phase: string;
  ok: boolean;
  error: string | null;
  kind: string | null;
  code: string | null;
}

interface OutboxItem {
  id: string;
  to: string;
  subject: string;
  status: "pending" | "parked" | "sent" | "abandoned";
  worker_retries: number;
  queued_at: string | null;
  last_tried_at: string | null;
  next_try_at: string | null;
  sent_at: string | null;
  error_kind: string | null;
  error_code: string | null;
  error_label: string | null;
  error_detail: string | null;
  history: Attempt[];
}

interface OutboxResponse {
  summary: {
    pending: number;
    parked: number;
    abandoned: number;
    sent_last_24h: number;
    oldest_stuck_at: string | null;
  };
  items: OutboxItem[];
  retry_policy: { inline: string; worker: string; parked: string; abandon_after_hours: number };
}

const STATUS_LABEL: Record<OutboxItem["status"], string> = {
  pending: "Retrying",
  parked: "Waiting for mail server",
  sent: "Sent after retry",
  abandoned: "Gave up",
};

function statusClass(status: OutboxItem["status"]): string {
  if (status === "sent") return "admin-badge is-active";
  if (status === "abandoned") return "admin-badge is-deactivated";
  if (status === "parked") return "admin-badge is-suspended";
  return "admin-badge is-trial";
}

function problem(row: Pick<OutboxItem, "error_label" | "error_code">): string {
  if (!row.error_label) return "—";
  return row.error_code ? `${row.error_label} (${row.error_code})` : row.error_label;
}

export function EmailErrorsPage() {
  const [data, setData] = useState<OutboxResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState("stuck");
  const [selected, setSelected] = useState<OutboxItem | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const row = await api.get<OutboxResponse>(`/operator/email-outbox?state=${encodeURIComponent(state)}`);
        if (!cancelled) {
          setData(row);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load the email outbox.");
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [state]);

  async function copyDetail() {
    if (!selected) return;
    const lines = [
      `To: ${selected.to}`,
      `Subject: ${selected.subject}`,
      `Status: ${STATUS_LABEL[selected.status]}`,
      `Queued: ${formatUtc(selected.queued_at)}`,
      `Last tried: ${formatUtc(selected.last_tried_at)}`,
      `Problem: ${problem(selected)}`,
      `Server said: ${selected.error_detail ?? "—"}`,
      "",
      ...selected.history.map(
        (a) => `${formatUtc(a.at)}  ${a.phase}  ${a.ok ? "ok" : `failed: ${a.code ?? ""} ${a.error ?? ""}`.trim()}`,
      ),
    ];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Could not copy to the clipboard. Select the text and copy it by hand.");
    }
  }

  const summary = data?.summary;
  const items = data?.items ?? [];
  const stuck = (summary?.pending ?? 0) + (summary?.parked ?? 0);

  return (
    <Stack gap={4}>
      <Stack gap={2}>
        <h2>Email errors</h2>
        <p className="admin-muted">
          Emails the mail server would not take. Each one is kept and re-sent automatically
          {data ? `: ${data.retry_policy.inline}, then ${data.retry_policy.worker}, then ${data.retry_policy.parked}` : ""}
          . Mail older than {data?.retry_policy.abandon_after_hours ?? 48} hours is given up on and also shows on Monitoring.
        </p>
      </Stack>
      {error && <Flash tone="error">{error}</Flash>}
      <Grid columns={4} gap={3}>
        <Card>
          <Stack gap={2}>
            <TipKicker
              label="Stuck now"
              tip="Emails not delivered yet: still being retried, or waiting for the mail server to come back. Zero is healthy."
            />
            <strong>{data ? stuck : "—"}</strong>
            <span className="admin-muted">
              {summary?.oldest_stuck_at ? `Oldest queued ${formatUtc(summary.oldest_stuck_at)}` : "Nothing waiting"}
            </span>
          </Stack>
        </Card>
        <Card>
          <Stack gap={2}>
            <TipKicker
              label="Waiting for mail server"
              tip="Retries are used up, so nothing is sent until the hourly check can connect and log in to the mail server."
            />
            <strong>{summary?.parked ?? "—"}</strong>
          </Stack>
        </Card>
        <Card>
          <Stack gap={2}>
            <TipKicker
              label="Recovered, last 24 hours"
              tip="Emails that failed at first and were delivered by a later retry."
            />
            <strong>{summary?.sent_last_24h ?? "—"}</strong>
          </Stack>
        </Card>
        <Card>
          <Stack gap={2}>
            <TipKicker
              label="Gave up"
              tip="Never delivered and now too old to be worth sending. Each one is also recorded on Monitoring."
            />
            <strong>{summary?.abandoned ?? "—"}</strong>
          </Stack>
        </Card>
      </Grid>
      <Card>
        <Stack gap={3}>
          <Cluster gap={3} align="end" justify="between">
            <TipHeading
              label="Emails"
              tip="Newest first. Last tried is the most recent send attempt; Next try is when the worker will try again."
            />
            <FormField label="Show" tooltip="Stuck is everything not yet delivered. The other views are for checking history.">
              <Select aria-label="Show emails" value={state} onChange={(event) => setState(event.target.value)}>
                <option value="stuck">Stuck (not delivered yet)</option>
                <option value="parked">Waiting for mail server</option>
                <option value="sent">Sent after retry</option>
                <option value="abandoned">Gave up</option>
                <option value="all">Everything</option>
              </Select>
            </FormField>
          </Cluster>
          <div className="admin-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Sent to</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Last tried (UTC)</th>
                  <th>Next try (UTC)</th>
                  <th>Problem</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>{row.to}</td>
                    <td>{row.subject}</td>
                    <td>
                      <span className={statusClass(row.status)}>{STATUS_LABEL[row.status]}</span>
                    </td>
                    <td>{formatUtc(row.last_tried_at)}</td>
                    <td>{row.next_try_at ? formatUtc(row.next_try_at) : "—"}</td>
                    <td>{problem(row)}</td>
                    <td>
                      <Button type="button" variant="secondary" size="sm" onClick={() => { setCopied(false); setSelected(row); }}>
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data && items.length === 0 && (
            <p className="admin-muted">
              {state === "stuck" ? "No emails are stuck. Everything is being delivered." : "No emails in this view."}
            </p>
          )}
        </Stack>
      </Card>
      <Modal
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? `Email to ${selected.to}` : "Email"}
        footer={(
          <Cluster gap={2}>
            <Button type="button" variant="secondary" onClick={() => void copyDetail()}>
              {copied ? "Copied" : "Copy details"}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setSelected(null)}>
              Close
            </Button>
          </Cluster>
        )}
      >
        {selected && (
          <Stack gap={3}>
            <p>Subject: {selected.subject}</p>
            <p>
              <span className={statusClass(selected.status)}>{STATUS_LABEL[selected.status]}</span>
              {" "}
              Queued {formatUtc(selected.queued_at)}
              {selected.sent_at ? `, sent ${formatUtc(selected.sent_at)}` : ""}
            </p>
            <p>Problem: {problem(selected)}</p>
            <p>Mail server said: {selected.error_detail ?? "—"}</p>
            <Stack gap={1}>
              <strong>Attempts</strong>
              <table>
                <thead>
                  <tr>
                    <th>When (UTC)</th>
                    <th>Step</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.history.map((attempt, index) => (
                    <tr key={`${attempt.at}-${index}`}>
                      <td>{formatUtc(attempt.at)}</td>
                      <td>{attempt.phase}</td>
                      <td>{attempt.ok ? "OK" : `${attempt.code ?? ""} ${attempt.error ?? "failed"}`.trim()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Stack>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
